/**
 * CetusEd Community Portal — Meeting Request Form
 *
 * COMPLIANCE: IDEA §300.322(a) — Parents may request an IEP team meeting
 *   at a mutually agreeable time. Section 504 §104.35(c) — Parents may
 *   request a 504 meeting. This form is the parent-initiated request path.
 *
 *   The school MUST respond to the request within a reasonable time
 *   (typically 10 school days per district policy). The request becomes
 *   part of the student's education record.
 */

import React, { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useRequestMeeting } from '../../api/hooks';
import { Button } from '../../ui/components/Button';

export const MeetingRequest: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const requestMeeting = useRequestMeeting();

  const [selectedStudentId, setSelectedStudentId] = useState(
    auth.user?.linkedStudents[0]?.id || '',
  );
  const [reason, setReason] = useState('');
  const [preferredDates, setPreferredDates] = useState<string[]>(['']);
  const [durationMinutes, setDurationMinutes] = useState(30);

  const handleAddDate = useCallback(() => {
    setPreferredDates((prev) => [...prev, '']);
  }, []);

  const handleDateChange = useCallback((index: number, value: string) => {
    setPreferredDates((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, []);

  const handleRemoveDate = useCallback((index: number) => {
    setPreferredDates((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedStudentId || !reason.trim() || preferredDates.every((d) => !d)) return;

      // mutate() routes failures into requestMeeting.isError, which is
      // rendered below — no swallowed rejection, no invisible failure.
      requestMeeting.mutate(
        {
          studentId: selectedStudentId,
          preferredDates: preferredDates.filter(Boolean),
          durationMinutes,
          reason: reason.trim(),
        },
        { onSuccess: () => navigate('/calendar', { replace: true }) },
      );
    },
    [selectedStudentId, reason, preferredDates, durationMinutes, requestMeeting, navigate],
  );

  if (!auth.user) return null;

  return (
    <div className="meeting-request-page">
      <nav className="breadcrumb">
        <Link to="/calendar">Calendar</Link> / Request Meeting
      </nav>

      <h1>Request a Meeting</h1>

      <form onSubmit={handleSubmit} className="meeting-request-form">
        <div className="form-field">
          <label htmlFor="mr-student">Regarding Student</label>
          <select
            id="mr-student"
            value={selectedStudentId}
            onChange={(e) => setSelectedStudentId(e.target.value)}
            required
          >
            <option value="">Select a student</option>
            {auth.user.linkedStudents.map((student) => (
              <option key={student.id} value={student.id}>
                {student.name} — {student.school}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="mr-reason">Reason for Meeting</label>
          <textarea
            id="mr-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            rows={4}
            maxLength={2000}
            placeholder="Describe the reason for the meeting request..."
          />
        </div>

        <div className="form-field">
          <label>Preferred Duration (minutes)</label>
          <select
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(Number(e.target.value))}
          >
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={45}>45 minutes</option>
            <option value={60}>60 minutes</option>
          </select>
        </div>

        <div className="form-field">
          <label>Preferred Dates and Times</label>
          {preferredDates.map((date, index) => (
            <div key={index} className="date-input-row">
              <input
                type="datetime-local"
                value={date}
                onChange={(e) => handleDateChange(index, e.target.value)}
              />
              {preferredDates.length > 1 && (
                <button
                  type="button"
                  className="remove-date-btn"
                  onClick={() => handleRemoveDate(index)}
                  aria-label="Remove date"
                >
                  &times;
                </button>
              )}
            </div>
          ))}
          <button type="button" className="link-button" onClick={handleAddDate}>
            + Add another date
          </button>
        </div>

        {requestMeeting.isError && (
          <div className="form-error" role="alert">
            Your meeting request could not be submitted. Please try again.
          </div>
        )}

        <div className="form-actions">
          <Button type="submit" disabled={requestMeeting.isPending} isLoading={requestMeeting.isPending}>
            Submit Request
          </Button>
          <Link to="/calendar">
            <Button variant="ghost">Cancel</Button>
          </Link>
        </div>
      </form>
    </div>
  );
};
